import {
  Loading,
  LoadingBlock,
  Table,
  TableColumnInstance,
} from '@orobix/orobix-fe-components'
import { AxiosError } from 'axios'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import InfiniteScroll from 'react-infinite-scroller'
import { useHistory, useParams } from 'react-router-dom'

import { TrainingTest, TrainingTests } from '../../store/store.types'
import { get } from '../../utils/api'
import { handleApiErrors } from '../../utils/apiErrors'
import { formatDate } from '../../utils/date'
import { locations } from '../../utils/locations'
import { getTrainingStatusNames, toScore } from '../../utils/trainings'
import useStyles from './DatasetTrainingsList.styles'

interface RouteParams {
  productId: string
  projectId: string
}

interface DatasetTestsListProps {
  datasetId: number
}

const itemsPerPage = 50

const DatasetTestsList = ({ datasetId }: DatasetTestsListProps) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const history = useHistory()
  const { productId, projectId } = useParams<RouteParams>()
  const [tests, setTests] = useState<TrainingTests | undefined>()
  const [isLoading, setIsLoading] = useState(false)
  const abortControllerRef = useRef<AbortController>()
  const isLoadingRef = useRef(false)

  const handleLoadMore = (page: number) => {
    if (isLoadingRef.current) return
    isLoadingRef.current = true
    setIsLoading(true)
    abortControllerRef.current = new AbortController()
    get<TrainingTests>(
      `/training_tests/?dataset=${datasetId}&limit=${itemsPerPage}&offset=${
        page * itemsPerPage
      }`,
      { signal: abortControllerRef.current.signal }
    )
      .then((data) => {
        setTests((prev) =>
          page !== 0 && prev
            ? { ...data, results: [...prev.results, ...data.results] }
            : data
        )
      })
      .catch((errors: AxiosError) => {
        handleApiErrors(errors)
      })
      .finally(() => {
        isLoadingRef.current = false
        setIsLoading(false)
      })
  }

  useEffect(
    () => () => {
      abortControllerRef.current?.abort()
    },
    []
  )

  const columns: TableColumnInstance<TrainingTest, keyof TrainingTest>[] = [
    {
      id: 'id',
      header: t('Id'),
      template: ({ id }) => `#${id}`,
    },
    {
      id: 'name',
      header: t('Name'),
    },
    {
      id: 'status',
      header: t('Status'),
      template: useCallback(
        ({ status }) => (
          <>
            {getTrainingStatusNames(status)}
            {status === 'RUNNING' && (
              <>
                {' '}
                <Loading size="0.9rem" />
              </>
            )}
          </>
        ),
        []
      ),
    },
    {
      id: 'overallPerformance',
      header: t('Performance'),
      template: ({ overallPerformance }) =>
        overallPerformance ? toScore(overallPerformance.data.value) : '-',
    },
    { id: 'createdBy', header: t('Created by') },
    {
      id: 'creationDate',
      header: t('Created on'),
      template: ({ creationDate }) => formatDate(creationDate),
    },
  ]

  return (
    <div>
      <InfiniteScroll
        pageStart={-1}
        loadMore={handleLoadMore}
        hasMore={
          tests ? tests.results.length < tests.count && !isLoading : !isLoading
        }
        loader={<LoadingBlock message={t('Loading')} key="loading" compact />}
      >
        <div className={classes.scrollWrapper}>
          <Table
            columns={columns}
            data={tests ? tests.results : []}
            alternateRows
            compact
            onRowClick={(ts) => {
              history.push(
                locations.trainingTest(
                  Number(projectId),
                  Number(productId),
                  ts.training,
                  ts.id
                )
              )
            }}
            isEmpty={tests && tests.count === 0 && tests.results.length <= 0}
            emptyMessage={t(
              `There's nothing here. Try creating a new {{ itemName }}.`,
              { itemName: t('test') }
            )}
          />
        </div>
        {tests && tests.results.length >= tests.count && tests.count > 0 && (
          <div className={classes.endOfList}>
            {t('All {{count}} records loaded', {
              count: tests.count,
            })}
          </div>
        )}
      </InfiniteScroll>
    </div>
  )
}

export default DatasetTestsList
